import * as React from 'react';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/material/styles/useTheme';
import Stack from "@mui/material/Stack";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
    DataGrid,
    GridActionsCellItem,
    GridCallbackDetails,
    GridColumns,
    GridEventListener,
    GridRowParams,
    MuiEvent
} from "@mui/x-data-grid";
import {
    useNavigate,
    useOutletContext
} from 'react-router-dom';
import {
    useQuery,
    UseQueryResult
} from 'react-query';
import NavBar from 'ui/components/classes-components/NavBar';
import { IOutletContext } from 'ui/components/Dashboard';
import { HttpClient } from 'apiclient/ApiClient';
import IClass from 'models/IClass';

export default function AllClassReports() {
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up('sm'));
    const navigate = useNavigate();
    const httpClient = HttpClient();
    const {
        setBgcolor,
        setTopBarColorLight
    } = useOutletContext<IOutletContext>();
    
    React.useEffect(() => {
        setBgcolor('background.paper');
        setTopBarColorLight("background.paper");
    }, [setBgcolor, setTopBarColorLight]);

    const classesGet: UseQueryResult<IClass[]> = useQuery(
        'classes', async () => await httpClient.get('classes')
    );



    // Show all classes without filtering by source
    const Classes = (Array.isArray(classesGet.data) ? classesGet.data : []).sort(
        (a, b) => (a.name).localeCompare(b.name)
    );

    // Add DEBUG class for testing
    // const debugClass = {
    //     id: 'FJ57MPdLiqENbbDpCy4lT3mmZyh4',
    //     name: 'DEBUG',
    //     number: 'Test',
    //     source: 'teacher' as const
    // };

    // const allClasses = [debugClass, ...Classes];
    const allClasses = [...Classes];

    const handleRowClick: GridEventListener<'rowClick'> = (
        params: GridRowParams,
        event: MuiEvent<React.MouseEvent<HTMLElement>>,
        details: GridCallbackDetails
    ) => {
        navigate(`/reports/${params.id}`)
    }

    const data_columns: GridColumns = [
        {   
            field: 'id',
            headerName: 'ID',
            editable: false,
            hide: true
        },
        {   
            field: 'num',
            headerName: '#',
            minWidth: 30,
            flex: 0.1,
            editable: false,
            disableColumnMenu: true,
            hideSortIcons: true,
            align: 'center',
            headerAlign: 'center',
            cellClassName: 'NoPadding',
            headerClassName: 'NoPadding'
        },
        {   
            field: 'name',
            headerName: 'Name',
            minWidth: 140,
            flex: 0.9,
            editable: false,
            disableColumnMenu: true
        },
        {   
            field: 'grade',
            headerName: 'Grade',
            minWidth: 200,
            flex: 1,
            editable: false,
            disableColumnMenu: true
        },
        {   
            field: 'actions',
            type: 'actions',
            flex: 0.25,
            minWidth: 60,
            align: 'center',
            hideSortIcons: true,
            disableColumnMenu: true,
            getActions: (params: GridRowParams) => {
                return([
                    <GridActionsCellItem
                        sx={{mx: 0, color: '#6A56CC'}}
                        icon={<ChevronRightIcon/>}
                        onClick={() => navigate(`/reports/${params.id}`)}
                        label='View reports'
                    />
                ])
            } 
        }
    ];
    // const rows = teacherClasses.map((classItem, index) => ({

    const rows = allClasses.map((classItem, index) => ({
        id: classItem.id,
        num: index + 1,
        name: classItem.name,
        grade: classItem.number || 'N/A',
    }));

    return (
        <Container maxWidth='lg' sx={{ pt: 2 }}>
            <Box sx={{ px: matches ? 6 : 0 }}>
                <NavBar />
                <Box sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: "space-between",
                    mb: 1,
                    mt: 6,
                    px: 0
                }}>
                    <Typography variant="h5" fontWeight={500} fontSize={32}>
                        All Class Reports
                    </Typography>
                </Box>
                
                <DataGrid
                    autoHeight
                    sortingOrder={['desc', 'asc']}
                    disableSelectionOnClick
                    columns={data_columns}
                    onRowClick={handleRowClick}
                    rows={rows}
                    hideFooter={true}
                    rowHeight={56}
                    headerHeight={70}
                    components={{
                        NoRowsOverlay: () => (
                            <Stack height="100%" alignItems="center" justifyContent="center">
                                No classes available
                            </Stack>
                        )
                    }}
                    componentsProps={{
                        row: {
                            style: {fontSize: 16, cursor: 'pointer'}
                        }
                    }}  
                    loading={classesGet.isLoading}
                />
            </Box>
        </Container>
    );
}